import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { Terminal, Sparkles, ArrowRight, CheckCircle2, AlertCircle, Mail, Brain, Users, Zap, Workflow, Plug, Database, GraduationCap, Megaphone, Rabbit, Search } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = "/api";
const APP_NAME = "ManiHQ";

/* =========================================================
   Helpers
========================================================= */
const LAUNCH_DATE = new Date("2026-10-25T00:00:00");

const PERSONAL_REGEX = /^[^\s@]+@(gmail|googlemail|yahoo|ymail|outlook|hotmail|live|msn|icloud|me|mac|proton|protonmail|pm|aol|zoho|gmx|mail|fastmail|tutanota|tuta|yandex|rediffmail)\.[a-z.]{2,10}$/i;

/* =========================================================
   Nav
========================================================= */
const Nav = () => (
  <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/40 border-b border-[#161616]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
      <a href="#top" className="flex items-center gap-3 group" data-testid="nav-logo">
        <img src="/logo.png" alt="ManiHQ logo" className="w-8 h-8 object-contain" />
        <span className="font-display font-bold tracking-tight text-[15px]">MANIHQ</span>
      </a>
      <nav className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-widest text-[#a3a3a3]">
        <a href="#features" className="hover:text-[#f3efea] transition-colors" data-testid="nav-features">Features</a>
        <a href="#agents" className="hover:text-[#f3efea] transition-colors" data-testid="nav-agents">Agents</a>
        <a href="#countdown" className="hover:text-[#f3efea] transition-colors" data-testid="nav-launch">Launch</a>
      </nav>
      <a
        href="#waitlist"
        data-testid="nav-cta"
        className="font-display font-bold uppercase text-[11px] tracking-widest bg-[#ff5a00] text-black px-4 py-2 hover:bg-[#ff8800] transition-colors"
      >
        Get started
      </a>
    </div>
  </header>
);

/* =========================================================
   Hero
========================================================= */
const Hero = () => {
  const { scrollY } = useScroll();
  const maniY = useTransform(scrollY, [0, 600], [0, -80]);
  const maniRot = useTransform(scrollY, [0, 600], [0, -6]);

  return (
    <section id="top" className="relative min-h-[100vh] flex items-center pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0 retro-grid" aria-hidden="true" />
      <div className="absolute inset-0 scanlines opacity-30" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid md:grid-cols-12 gap-10 items-center relative">
        {/* Left: copy */}
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff8800] flex items-center gap-3"
            data-testid="hero-tagline"
          >
            <span className="w-8 h-px bg-[#ff8800]" />
            SYSTEM.INITIALIZE()
            <span className="cursor-blink text-[#ff5a00]">▍</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display font-black uppercase mt-6 tracking-tighter leading-[0.9] text-[clamp(2.6rem,7vw,5.6rem)]"
            data-testid="hero-headline"
          >
            Your AI team,
            <br />
            <span className="text-[#ff5a00]">in one</span> workspace.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 max-w-xl text-[15px] md:text-base text-[#a3a3a3] leading-relaxed"
            data-testid="hero-subhead"
          >
            ManiHQ replaces tabs, chatbots, and copy-paste with a small office of AI agents
            that actually talk to each other — and to you — to get real work done.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#waitlist"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 bg-[#ff5a00] text-black font-display font-bold uppercase tracking-widest text-xs px-7 py-4 border-2 border-[#ff5a00] hover:bg-[#ff8800] hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#f3efea] transition-all"
            >
              Get started
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#features"
              className="font-mono uppercase text-xs tracking-widest text-[#a3a3a3] hover:text-[#f3efea] flex items-center gap-2"
              data-testid="hero-secondary"
            >
              <span className="w-6 h-px bg-current" />
              See features
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-14 font-mono text-[11px] uppercase tracking-widest text-[#666] flex items-center gap-6"
          >
            <span>v0.1 · private beta</span>
            <span>·</span>
            <span>aws bedrock</span>
            <span>·</span>
            <span>end-to-end agents</span>
          </motion.div>
        </div>

        {/* Right: mascot */}
        <div className="md:col-span-5 relative flex items-center justify-center">
          <motion.div
            style={{ y: maniY, rotate: maniRot }}
            className="relative"
          >
            <div className="absolute -inset-10 bg-[#ff5a00]/20 blur-3xl rounded-full" aria-hidden="true" />
            <motion.img
              src="/mani.png"
              alt="Mani — Manifold's pixel-art bunny mascot, holding a terminal"
              className="mani-halo mani-float w-[280px] md:w-[420px] relative z-10"
              data-testid="hero-mascot"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
            {/* sparkles */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="absolute -top-2 -right-2 text-[#ff8800]"
            >
              <Sparkles className="w-6 h-6" />
            </motion.div>
            {/* speech bubble */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black border border-[#ff5a00] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-[#ff8800] whitespace-nowrap"
            >
              ▸ mani is here<span className="cursor-blink ml-1 text-[#ff5a00]">_</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* scroll hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#666] flex items-center gap-2">
        scroll
        <span className="block w-px h-6 bg-[#444]" />
      </div>
    </section>
  );
};

/* =========================================================
   Features
========================================================= */
const FEATURES = [
  {
    id: "shared-intelligence",
    tag: "F-01",
    title: "Shared Intelligence",
    subtitle: "Never explain yourself twice.",
    description: "Your AI team shares a unified understanding of your projects. When you brief Mani, every agent gets the memo. No more repeating yourself across different tools.",
    Icon: Brain,
  },
  {
    id: "ai-teamwork",
    tag: "F-02",
    title: "AI Teamwork",
    subtitle: "Multiple AI Experts. One Workspace.",
    description: "Research, writing, coding, planning — each agent specializes and collaborates. They hand off tasks, share insights, and work together like a real office team.",
    Icon: Users,
  },
  {
    id: "specialized-ai",
    tag: "F-03",
    title: "Specialized AI",
    subtitle: "The Right Expert For Every Task",
    description: "Atlas for deep research. Vellum for writing. Cinder for code. Beacon for scheduling. Deploy the perfect agent for every job.",
    Icon: Zap,
  },
  {
    id: "dynamic-workflows",
    tag: "F-04",
    title: "Dynamic Workflows",
    subtitle: "Complex Tasks. Fully Automated.",
    description: "Break down complex projects into automated workflows. Your agents divide tasks, execute in parallel, and assemble results — all without human bottlenecks.",
    Icon: Workflow,
  },
  {
    id: "connect-everything",
    tag: "F-05",
    title: "Connect Everything",
    subtitle: "Work with tools you already use.",
    description: "Seamlessly integrates with Gmail, Google Drive, Notion, Slack, GitHub, Calendar, and more. Your AI team works inside your existing stack.",
    Icon: Plug,
  },
  {
    id: "persistent-memory",
    tag: "F-06",
    title: "Persistent Memory",
    subtitle: "Your workspace remembers everything.",
    description: "Every conversation, document, and decision is retained. Pick up where you left off. Your AI team learns your preferences and improves over time.",
    Icon: Database,
  },
];

const ScrollNarrative = () => {
  return (
    <section id="features" className="relative py-28 md:py-36 border-t border-[#161616]">
      <div className="absolute inset-0 retro-grid opacity-40" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff8800] flex items-center justify-center gap-3" data-testid="features-eyebrow">
            <span className="w-8 h-px bg-[#ff8800]" />
            CAPABILITIES
            <span className="w-8 h-px bg-[#ff8800]" />
          </div>
          <h2 className="font-display font-black uppercase text-3xl md:text-5xl mt-5 tracking-tighter" data-testid="features-title">
            Features.
          </h2>
          <p className="mt-5 text-[#a3a3a3] text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Everything you need to build an AI-powered workflow.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {FEATURES.map((feature, i) => (
            <motion.article
              key={feature.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`feature-card-${feature.id}`}
              className="group relative border border-[#1f1f1f] bg-[#0c0c0c] p-6 md:p-7 hover:border-[#ff5a00]/40 hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-[#666] mb-5">
                <span>[{feature.tag}]</span>
                <span className="text-[#ff8800]">○ active</span>
              </div>

              <div className="w-11 h-11 grid place-items-center border border-[#ff5a00]/40 bg-black mb-5 group-hover:border-[#ff5a00] transition-colors">
                <feature.Icon className="w-5 h-5 text-[#ff5a00]" strokeWidth={1.5} />
              </div>

              <h3 className="font-display font-black uppercase text-xl tracking-tight text-[#f3efea]">{feature.title}</h3>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-[#ff8800]">{feature.subtitle}</p>
              <p className="mt-4 text-sm text-[#a3a3a3] leading-relaxed">{feature.description}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};




/* =========================================================
   Agents roster
========================================================= */
const AGENTS = [
  { tag: "M-00", name: "Mani", role: "Chief of Staff", desc: "Your enthusiastic AI manager. Mani delegates, follows up, and keeps the team in sync.", status: "live", featured: true },
  { tag: "R-01", name: "Atlas", role: "Researcher", desc: "Scrapes the deep web, ranks sources, never copies homework.", status: "soon" },
  { tag: "W-02", name: "Vellum", role: "Writer", desc: "Drafts emails, essays and replies in your voice.", status: "soon" },
  { tag: "C-03", name: "Cinder", role: "Code Assistant", desc: "Reads repos, writes patches, runs the tests.", status: "soon" },
  { tag: "P-04", name: "Beacon", role: "Planner", desc: "Owns your calendar, blocks deep work, kills meetings.", status: "soon" },
  { tag: "S-05", name: "Echo", role: "Social Manager", desc: "Drafts posts, schedules, monitors mentions.", status: "soon" },
];

const Agents = () => (
  <section id="agents" className="relative py-28 md:py-36 border-t border-[#161616]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff8800] flex items-center gap-3" data-testid="roster-eyebrow">
            <span className="w-8 h-px bg-[#ff8800]" />
            THE ROSTER
          </div>
          <h2 className="font-display font-black uppercase text-3xl md:text-5xl mt-3 tracking-tighter" data-testid="roster-title">
            Meet the team.
          </h2>
        </div>
        <p className="max-w-md text-sm text-[#a3a3a3] leading-relaxed">
          One manager, a roster of specialists. New agents join the office every few weeks — you pick who&apos;s on the payroll.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {AGENTS.map((a, i) => (
          <motion.div
            key={a.tag}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
            data-testid={`roster-card-${a.tag}`}
            className={`relative border border-[#1f1f1f] bg-[#0c0c0c] p-6 md:p-7 hover:border-[#ff5a00]/40 transition-colors group ${a.featured ? "col-span-12 md:col-span-7 row-span-2 md:p-10" : "col-span-12 sm:col-span-6 md:col-span-5"
              }`}
          >
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-[#666] mb-4">
              <span>[{a.tag}]</span>
              <span className={a.status === "live" ? "text-[#4af626]" : "text-[#ff8800]"}>
                {a.status === "live" ? "● live" : "○ coming soon"}
              </span>
            </div>

            {a.featured && (
              <img
                src="/mani.png"
                alt="Mani"
                className="mani-halo w-32 md:w-40 mb-6"
              />
            )}

            <div className="font-mono text-[11px] uppercase tracking-widest text-[#ff8800] mb-2">{a.role}</div>
            <h3 className={`font-display font-black uppercase tracking-tighter ${a.featured ? "text-4xl md:text-5xl" : "text-2xl"}`}>
              {a.name}
            </h3>
            <p className="mt-3 text-sm text-[#a3a3a3] leading-relaxed max-w-md">{a.desc}</p>

            {a.featured && (
              <div className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-widest text-[#ff5a00] border border-[#ff5a00]/40 px-2 py-1">
                manager
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* =========================================================
   Countdown
========================================================= */
const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
};

const Countdown = () => {
  const t = useCountdown(LAUNCH_DATE);
  const pad = (n) => String(n).padStart(2, "0");
  const items = [
    { label: "days", value: pad(t.days) },
    { label: "hours", value: pad(t.hours) },
    { label: "min", value: pad(t.minutes) },
    { label: "sec", value: pad(t.seconds) },
  ];

  const launchStr = LAUNCH_DATE.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

  return (
    <section id="countdown" className="relative py-28 md:py-36 border-t border-[#161616]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff8800] flex items-center gap-3" data-testid="countdown-eyebrow">
          <span className="w-8 h-px bg-[#ff8800]" />
          OFFICE OPENS IN
        </div>
        <h2 className="font-display font-black uppercase text-3xl md:text-5xl mt-3 tracking-tighter" data-testid="countdown-title">
          T-minus launch.
        </h2>

        <div className="mt-10 grid grid-cols-4 gap-3 md:gap-6 max-w-3xl" data-testid="countdown-grid">
          {items.map((it) => (
            <div key={it.label} className="bg-black border border-[#1f1f1f] p-4 md:p-6 text-center relative overflow-hidden">
              <div className="absolute inset-0 scanlines opacity-30" aria-hidden="true" />
              <div
                className="font-display font-black text-4xl md:text-6xl text-[#ff5a00] tabular-nums tracking-tighter relative z-10"
                data-testid={`countdown-${it.label}`}
              >
                {it.value}
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#666] relative z-10">{it.label}</div>
            </div>
          ))}
        </div>

        <p className="mt-6 font-mono text-xs uppercase tracking-widest text-[#a3a3a3]" data-testid="countdown-date">
          target launch · {launchStr}
        </p>
      </div>
    </section>
  );
};

/* =========================================================
   Waitlist
========================================================= */
const TOTAL_SEATS = 100;

const USE_CASE_OPTIONS = [
  { value: "study_helper", label: "Study Helper", hint: "Notion, Apify", Icon: GraduationCap },
  { value: "deep_search", label: "Deep Search", hint: "Tavily, Exa", Icon: Search },
  { value: "social_media", label: "Social Media Manager", hint: "drafts + scheduling", Icon: Megaphone },
  { value: "cooperative_swarms", label: "Cooperative Swarms", hint: "multi-agent flows", Icon: Users },
  { value: "just_curious", label: "Just curious", hint: "show me what Mani can do", Icon: Rabbit },
];

const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [useCase, setUseCase] = useState("study_helper");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [position, setPosition] = useState(null);
  const [count, setCount] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => { });
  }, []);

  const seatsLeft = useMemo(() => {
    if (count === null) return null;
    return Math.max(0, TOTAL_SEATS - count);
  }, [count]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    const value = email.trim().toLowerCase();
    if (!value) {
      setErrMsg("Drop an email first.");
      return;
    }
    if (!PERSONAL_REGEX.test(value)) {
      setErrMsg("Personal email only — try gmail, outlook, icloud, yahoo, proton, etc.");
      toast.error("Personal email only", { description: "Work and school emails aren't allowed." });
      return;
    }

    setStatus("loading");
    try {
      const res = await axios.post(`${API}/waitlist`, { email: value, use_case: useCase });
      setStatus("success");
      setPosition(res.data.position);
      setCount((c) => (c == null ? res.data.position : Math.max(c, res.data.position)));
      toast.success("You're in!", { description: `Mani saved your spot · #${res.data.position}` });
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      setErrMsg(typeof detail === "string" ? detail : "Invalid request.");
      toast.error("Couldn't join", { description: typeof detail === "string" ? detail : "Invalid request." });
    }
  };

  return (
    <section id="waitlist" className="relative py-28 md:py-40 border-t border-[#161616] bg-[#070707]">
      <div className="absolute inset-0 retro-grid opacity-40" aria-hidden="true" />
      <div className="relative max-w-3xl mx-auto px-6 md:px-12">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff8800] flex items-center justify-center gap-3" data-testid="waitlist-eyebrow">
          <span className="w-8 h-px bg-[#ff8800]" />
          JOIN THE MANIHQ SANDBOX WAITLIST
          <span className="w-8 h-px bg-[#ff8800]" />
        </div>

        <h2 className="font-display font-black uppercase text-4xl md:text-6xl mt-5 tracking-tighter text-center" data-testid="waitlist-title">
          Get on the list.
        </h2>
        <p className="mt-5 text-[#a3a3a3] text-center text-sm md:text-base leading-relaxed max-w-xl mx-auto" data-testid="waitlist-sub">
          Early seats unlock first. Personal email only — work and school addresses are blocked.
        </p>

        <AnimatePresence mode="wait">
          {status !== "success" ? (
            <motion.form
              key="form"
              onSubmit={onSubmit}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="mt-10"
              noValidate
            >
              {/* Seats remaining */}
              {seatsLeft !== null && (
                <div
                  data-testid="seats-remaining"
                  className="mb-8 border border-[#ff5a00]/40 bg-[#ff5a00]/[0.06] p-4 md:p-5 flex items-center gap-4"
                >
                  <span className="relative flex h-3 w-3 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff5a00] opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#ff5a00]" />
                  </span>
                  <div className="text-left">
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff5a00] mb-1">
                      LIMITED SEATS REMAINING
                    </div>
                    <div className="font-mono text-sm text-[#a3a3a3]">
                      Only{" "}
                      <span className="text-[#f3efea] font-bold" data-testid="seats-left-number">
                        {seatsLeft} of {TOTAL_SEATS} seats
                      </span>{" "}
                      are left before registration locks.
                    </div>
                  </div>
                </div>
              )}

              {/* Use case picker */}
              <fieldset className="text-left">
                <legend className="font-display text-sm md:text-base text-[#f3efea] mb-4 tracking-tight">
                  Which agent swarm are you most excited to deploy?
                </legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Use case">
                  {USE_CASE_OPTIONS.map((opt) => {
                    const selected = useCase === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setUseCase(opt.value)}
                        data-testid={`use-case-${opt.value}`}
                        className={`group flex items-center gap-3 text-left px-4 py-3.5 border-2 transition-all ${selected
                            ? "border-[#ff5a00] bg-[#ff5a00]/[0.07] shadow-[3px_3px_0_#ff5a00]"
                            : "border-[#1f1f1f] bg-black hover:border-[#ff5a00]/50"
                          }`}
                      >
                        <span
                          className={`w-9 h-9 grid place-items-center shrink-0 border ${selected ? "border-[#ff5a00] bg-black" : "border-[#262626] bg-black"
                            }`}
                        >
                          <opt.Icon
                            className={`w-4 h-4 ${selected ? "text-[#ff5a00]" : "text-[#a3a3a3] group-hover:text-[#ff8800]"}`}
                            strokeWidth={1.6}
                          />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className={`block font-display font-bold uppercase text-[13px] tracking-tight ${selected ? "text-[#f3efea]" : "text-[#f3efea]"}`}>
                            {opt.label}
                          </span>
                          <span className="block font-mono text-[10px] uppercase tracking-widest text-[#666]">
                            {opt.hint}
                          </span>
                        </span>
                        <span
                          className={`w-3 h-3 rounded-full border ${selected ? "border-[#ff5a00] bg-[#ff5a00]" : "border-[#333]"
                            }`}
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              {/* Email + submit */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <div className={`flex-1 brutal-focus flex items-center bg-black border-2 ${errMsg ? "border-[#ff5a00]" : "border-[#222]"} focus-within:border-[#ff5a00]`}>
                  <span className="pl-4 text-[#666]"><Mail className="w-4 h-4" /></span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrMsg(""); }}
                    placeholder="you@personal-email.com"
                    data-testid="waitlist-email-input"
                    aria-label="Personal email"
                    className="flex-1 bg-transparent px-3 py-4 font-mono text-sm text-[#f3efea] placeholder:text-[#555] focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="waitlist-submit-button"
                  className="group inline-flex items-center justify-center gap-3 bg-[#ff5a00] text-black font-display font-bold uppercase tracking-widest text-xs px-7 py-4 border-2 border-[#ff5a00] hover:bg-[#ff8800] hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#f3efea] transition-all disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  {status === "loading" ? "Saving…" : (<>Get started <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-10 border-2 border-[#ff5a00] bg-black p-6 md:p-8 text-left"
              data-testid="waitlist-success"
            >
              <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-[#4af626]">
                <CheckCircle2 className="w-4 h-4" />
                spot saved
              </div>
              <h3 className="font-display font-black uppercase text-2xl md:text-3xl mt-3">
                You&apos;re #{position} on the list.
              </h3>
              <p className="mt-2 text-sm text-[#a3a3a3]">
                Mani is going to ping <span className="text-[#f3efea]">{email}</span> the moment the office opens. Until then, keep an eye on your inbox.
              </p>
              {seatsLeft !== null && (
                <div className="mt-5 font-mono text-[11px] uppercase tracking-widest text-[#ff8800]">
                  {seatsLeft} / {TOTAL_SEATS} seats remaining
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {errMsg && status !== "success" && (
          <div className="mt-4 inline-flex items-center gap-2 font-mono text-xs text-[#ff8800]" data-testid="waitlist-error">
            <AlertCircle className="w-3.5 h-3.5" /> {errMsg}
          </div>
        )}

        {count !== null && status !== "success" && (
          <div className="mt-8 font-mono text-[11px] uppercase tracking-widest text-[#666] text-center" data-testid="waitlist-count">
            * Personal addresses only. Corporate or school domains will be blocked.
          </div>
        )}
      </div>
    </section>
  );
};

/* =========================================================
   Footer
========================================================= */
const Footer = () => (
  <footer className="border-t border-[#161616] py-12">
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src="/logo.png" alt="ManiHQ logo" className="w-8 h-8 object-contain" />
        <div>
          <div className="font-display font-bold tracking-tight text-sm">MANIHQ</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#666]">an ai-native workspace · © {new Date().getFullYear()}</div>
        </div>
      </div>
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#666] flex items-center gap-2" data-testid="footer-built-with">
        <Terminal className="w-3.5 h-3.5" />
        built with mani<span className="cursor-blink text-[#ff5a00]">_</span>
      </div>
    </div>
    <div className="mt-8 ascii-divider text-center text-[10px]">
      ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─
    </div>
  </footer>
);

/* =========================================================
   Page
========================================================= */
export default function App() {
  return (
    <div className="relative min-h-screen bg-[#050505] text-[#f3efea] grain">
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid #222",
            color: "#f3efea",
            fontFamily: "'JetBrains Mono', monospace",
            borderRadius: 0,
          },
        }}
      />
      <Nav />
      <main>
        <Hero />
        <ScrollNarrative />
        <Agents />
        <Countdown />
        <Waitlist />
      </main>
      <Footer />
    </div>
  );
}
